import {
  useBacktrack,
  withBacktrack,
} from 'components/common/withBacktrack';
import { withIssueForm } from 'components/common/withIssueForm';
import { withProjectChangeDialog } from 'components/common/withProjectChangeDialog';
import { useProjects } from 'store/projects';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { Spinner } from 'components/core';
import { IssueViewLoadingShell } from '../IssueViewLoadingShell';
import { WithCreateSingleIssueReport } from 'components/dataCreationForms/report/withCreateSingleIssueReport';
import { WithCreateSingleInspectionReport } from 'components/dataCreationForms/report/withCreateSingleInspectionReport';
import {
  useCurrentIssue,
  useCurrentIssueDocuments,
  useCurrentIssueForm,
  useCurrentIssueMissingReference,
  withIssue,
} from 'components/dataProviders/withIssue';
import { useProcesses } from 'store/process';
import { useSyncIssuesOnMount } from 'hooks/useSyncIssuesOnMount';
import { useIssueIdListLoader, useIssueStore } from 'store/issue';
import {
  WithExportToDocumentation,
} from 'components/documentation/ExportToDocumentationDialog';
import {
  WithImportFromDocumentation,
} from 'components/documentation/ImportFromDocumentationDialog';
import { DirectoryDialog } from 'views/documentations/directory/dialog';
import { composeHOCs } from 'shared/utils/composeHOCs';
import React, {
  memo,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_PATHS } from 'setup/navigation/paths';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useProject } from 'store/project';
import { ProjectInfo } from 'setup/types/core';
import { DeepLinkTarget, EntityStatuses, EntityStatusType } from './types';
import {
  getRouteProjectState,
  resolveDeepLink,
  isSameEntityStatus,
  resolveEntityStatus,
  resolveIssueEntityStatus,
} from './model';

const IssueView = React.lazy(
  () => import('components/issue/UnifiedIssue')
);
const EntityRemovedView = React.lazy(() => import('../EntityRemovedView'));
const EntityNotFoundView = React.lazy(
  () => import('../EntityNotFoundView')
);

interface IssueViewContainerProps {
  projectData: ProjectInfo;
}

const IssueViewContainer: React.FC<IssueViewContainerProps> = ({
  projectData,
}) => {
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const issueId = useAppUrlParams(UrlParamName.ISSUE_ID);
  const eventId = useAppUrlParams(UrlParamName.EVENT_ID);
  const documentId = useAppUrlParams(UrlParamName.DOCUMENT_ID);
  const linkedDocumentationVersionId = useAppUrlParams(
    UrlParamName.LINKED_DOCUMENTATION_VERSION_ID
  );
  const { back } = useBacktrack();
  const navigate = useNavigate();
  const location = useLocation();
  // ready to take issue from state when _id not defined
  const stateIssue = location.state?.issue;

  const issue = useCurrentIssue();
  const error = useIssueStore.use.currentIssueError();
  const missingReference = useCurrentIssueMissingReference();
  const issueLoadState = useMemo(() => ({ hasError: error, missingReference }), [error, missingReference]);
  const loading = !issue && !error;
  const { documents, loading: documentsLoading } = useCurrentIssueDocuments();
  const { formReady } = useCurrentIssueForm();

  const [entityStatus, setEntityStatus] = useState<
    EntityStatusType | undefined
  >(resolveIssueEntityStatus(undefined, issueLoadState));

  useEffect(() => {
    if (!(issueId || stateIssue?._id || stateIssue?.localId)) {
      // TODO: show error?
      back();
    }
  }, [issueId, stateIssue, back]);

  useSyncIssuesOnMount();


  const isIssueRouteReady = Boolean(
    issue &&
      issueId &&
      issue._id === issueId &&
      !loading &&
      formReady
  );

  const { items: projects, loading: projectsLoading } = useProjects();
  const { isSelected, isProjectListLoaded, isAccessible } =
    getRouteProjectState(
      projectId,
      projectData._id,
      projects,
      !projectsLoading
    );
  // Updated during render, not in an effect: nothing renders with an outdated status.
  const resolvedEntityStatus = resolveEntityStatus(entityStatus, issueLoadState, {
    isSelected,
    isProjectListLoaded,
    isAccessible,
  });
  if (!isSameEntityStatus(resolvedEntityStatus, entityStatus)) {
    setEntityStatus(resolvedEntityStatus);
  }

  const deepLink = useMemo(
    () =>
      issue && !loading && !documentsLoading
        ? resolveDeepLink(
            { eventId, documentId, linkedDocumentationVersionId },
            issue,
            documents,
            Boolean(projectId && issueId)
          )
        : undefined,
    [
      issue,
      loading,
      documentsLoading,
      eventId,
      documentId,
      linkedDocumentationVersionId,
      documents,
      projectId,
      issueId,
    ]
  );

  // A status once shown stays: the deep link is followed only while there is none.
  useEffect(() => {
    if (entityStatus || !deepLink) {
      return;
    }
    if (deepLink.target === DeepLinkTarget.missing) {
      setEntityStatus(deepLink.status);
    } else if (deepLink.target === DeepLinkTarget.issue && projectId && issueId) {
      navigate(APP_PATHS.issue.single(projectId, issueId), { replace: true });
    }
  }, [entityStatus, deepLink, navigate, projectId, issueId]);

  if (entityStatus) {
    const { entity, status } = entityStatus;
    const { NOT_FOUND, DELETED } = EntityStatuses;

    if (status === NOT_FOUND) {
      return (
        <Suspense
          fallback={<Spinner reason='loading EntityNotFoundView' />}
        >
          <EntityNotFoundView entity={entity} />
        </Suspense>
      );
    }
    if (status === DELETED) {
      return (
        <Suspense
          fallback={<Spinner reason='loading EntityRemovedView' />}
        >
          <EntityRemovedView entity={entity} />
        </Suspense>
      );
    }
  }

  if (issueId && !isIssueRouteReady) {
    const issueNcrNumber =
      issue?._id === issueId
        ? issue.ncrNumber
        : stateIssue?.ncrNumber;

    return <IssueViewLoadingShell issueNcrNumber={issueNcrNumber} />;
  }

  if (!issue) {
    return null;
  }

  //TODO https://hustro.atlassian.net/browse/PT-2401
  return (
    <>
      {/* Sole mount for issue documentation import/export (not on inspection views). */}
      <WithImportFromDocumentation>
        <WithExportToDocumentation>
          <WithCreateSingleIssueReport>
            <WithCreateSingleInspectionReport>
              <ViewWrapper>
                <IssueView />
              </ViewWrapper>
            </WithCreateSingleInspectionReport>
          </WithCreateSingleIssueReport>
          <DirectoryDialog />
        </WithExportToDocumentation>
      </WithImportFromDocumentation>
    </>
  );
};

/**
 * It prevents rendering Container if project/process data is not available yet (after login in particular)
 * @constructor
 */
const ContainerDataProvider = (): React.ReactElement => {
  const projectData = useProject();
  const { processes: processesFromStore, loading: processesLoading } = useProcesses();
  const cachedIssue = useCurrentIssue();
  const location = useLocation();
  // Above the view remounted per issue: the navigation list is read once, not on every issue.
  useIssueIdListLoader();

  const issueId = useAppUrlParams(UrlParamName.ISSUE_ID);
  const stateIssue = location.state?.issue;
  const issueNcrNumber =
    cachedIssue?._id === issueId
      ? cachedIssue.ncrNumber
      : stateIssue?.ncrNumber;

  const isDataLoaded =
    Boolean(projectData._id) && !processesLoading && processesFromStore.length > 0;

  return isDataLoaded ? (
    <IssueViewContainer
      key={issueId} //required to remount component when route changes after creating similar issue
      projectData={projectData}
    />
  ) : (
    <IssueViewLoadingShell
      spinnerReason='loading issue view data'
      issueNcrNumber={issueNcrNumber}
    />
  );
};

export default composeHOCs(
  withIssueForm,
  withIssue,
  withProjectChangeDialog
)(memo(ContainerDataProvider));
